/** @fileoverview Keeps the hosted app pass in a host-only cookie for this apps-domain page. */

const PASS_COOKIE = '__Host-fabrik_pass';

type SavePassProps = {
  token: string;
  expiresIn: number;
};

let memoryPass = '';
let memoryPassExpiresAt = 0;
let hasWarned = false;

/** Reads the pass from the cookie, or '' when there is none. */
const readCookiePass = () => {
  try {
    const entry = document.cookie.split('; ').find((part) => part.startsWith(`${PASS_COOKIE}=`));
    return entry ? decodeURIComponent(entry.slice(PASS_COOKIE.length + 1)) : '';
  } catch {
    return '';
  }
};

/** Saves the pass in a cookie, and keeps it in memory for this page load if the browser refuses the cookie. */
export const saveAppPass = ({ token, expiresIn }: SavePassProps) => {
  memoryPass = '';
  memoryPassExpiresAt = 0;
  const expiresAt = Date.now() + expiresIn * 1000;
  try {
    document.cookie = `${PASS_COOKIE}=${encodeURIComponent(
      token
    )}; Max-Age=${expiresIn}; Path=/; Secure; SameSite=Strict`;
  } catch {
    // The check below handles a cookie that did not stick.
  }
  if (readCookiePass() !== token) {
    memoryPass = token;
    memoryPassExpiresAt = expiresAt;
    if (!hasWarned) {
      hasWarned = true;
      console.warn(
        'The browser did not keep the app pass cookie. Using memory for this page load.'
      );
    }
  }
};

/** Returns the saved pass, or '' when there is none. */
export const readAppPass = () => {
  if (memoryPass && memoryPassExpiresAt <= Date.now()) {
    memoryPass = '';
    memoryPassExpiresAt = 0;
  }
  return readCookiePass() || memoryPass;
};

/** Returns the saved pass expiry time, or 0 when unavailable. */
export const readAppPassExpiresAt = () => {
  const payload = readAppPass().split('.')[1];
  if (!payload) return memoryPassExpiresAt;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
    const expiry = Number((JSON.parse(decoded) as { exp?: number }).exp) * 1000;
    return Number.isFinite(expiry) && expiry > 0 ? expiry : memoryPassExpiresAt;
  } catch {
    return memoryPassExpiresAt;
  }
};

/** Removes the saved pass. */
export const clearAppPass = () => {
  memoryPass = '';
  memoryPassExpiresAt = 0;
  try {
    document.cookie = `${PASS_COOKIE}=; Max-Age=0; Path=/; Secure; SameSite=Strict`;
  } catch {
    // Nothing else to clear.
  }
};
